import React, { useEffect, useRef } from "react";
import { Facility } from "@/types";
import { useTranslation } from "react-i18next";

interface LeafletMapProps {
  facilities: Facility[];
  handleDetails: (id: number) => void;
}

const LeafletMap: React.FC<LeafletMapProps> = ({
  facilities,
  handleDetails,
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const { t } = useTranslation();

  // Initialize the map on first render
  useEffect(() => {
    // Only execute this code on the client
    if (typeof window === "undefined") return;

    // Dynamically import Leaflet
    const initializeMap = async () => {
      // Import Leaflet
      const L = await import("leaflet");

      // Fix Leaflet icon issues
      delete (L.Icon.Default.prototype as any)._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl:
          "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
        iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
        shadowUrl:
          "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
      });

      // If we already have a map instance, remove it
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
      }

      // Create a new map instance
      if (mapContainerRef.current) {
        // Initialize map
        const map = L.map(mapContainerRef.current).setView(
          [40.416775, -3.70379],
          6,
        );

        // Add tile layer
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(map);

        // Add markers for facilities with valid coordinates
        const validFacilities = facilities.filter(
          (facility) =>
            facility.lat !== null &&
            facility.lng !== null &&
            facility.lat !== undefined &&
            facility.lng !== undefined,
        );

        // Create markers
        const markers = validFacilities
          .map((facility) => {
            if (facility.lat && facility.lng) {
              const marker = L.circleMarker([facility.lat, facility.lng], {
                radius: 12, // size of the marker
                color: "#17824D", // stroke color
                weight: 2, // stroke width
                fillColor: "#17824D", // fill color
                fillOpacity: 0.8, // opacity of the fill
              }).addTo(map).bindPopup(`
                <div>
                  <h3 style="font-weight: bold;">${facility.name}</h3>
                  <button 
                    id="facility-${facility.id}"
                    style="color: #6BE600; margin-top: 8px;"
                  >
                    ${t("facilities.details")}
                  </button>
                </div>
              `);

              // Add click event listener to the popup content after it's opened
              marker.on("popupopen", () => {
                setTimeout(() => {
                  const button = document.getElementById(
                    `facility-${facility.id}`,
                  );
                  if (button) {
                    button.addEventListener("click", () =>
                      handleDetails(facility.id),
                    );
                  }
                }, 0);
              });

              return marker;
            }
            return null;
          })
          .filter(Boolean);

        // Fit bounds if we have markers
        if (markers.length > 0) {
          const group = L.featureGroup(markers as any);
          map.fitBounds(group.getBounds(), { padding: [50, 50] });
        }

        // Save map instance for cleanup
        mapInstanceRef.current = map;
      }
    };

    initializeMap();

    // Cleanup function
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [facilities, handleDetails]);

  return (
    <div
      ref={mapContainerRef}
      style={{ height: "100%", width: "100%", position: "relative", zIndex: 0 }}
    />
  );
};

export default LeafletMap;
